/**
 * Árbol de estructura del modelo (`/api/componentes`).
 *
 * La estructura cuelga del MODELO y la comparten todas sus versiones. Solo la
 * raíz lleva `id_modelo`; los hijos lo tienen en null y se resuelven por
 * traversal. Solo es editable con el MODELO en estado `edicion`.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import Componente from '@/interfaces/Componente'
import { http } from '@/services/http'

/** Forma con la que el API de componente devuelve este dato. */
export interface ComponenteResponse {
  status: boolean
  componente?: Componente
}

/** Forma con la que el API de componente devuelve este dato. */
export interface EliminarResponse {
  status: boolean
}

/**
 * Consulta componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Componente[]
 */
export async function getTodosComponentesService(url: string): Promise<Componente[]> {
  const result = await http.get<Componente[]>(url)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Consulta componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Componente | null
 */
export async function getComponentePorIdService(url: string, id: number): Promise<Componente | null> {
  const result = await http.get<Componente>(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Crea componente.
 *
 * @returns Componente | null
 */
export async function crearComponenteRaizService(
  url: string,
  data: { id_modelo: number; nombre: string; peso?: number; descripcion?: string | null }
): Promise<Componente | null> {
  const payload = {
    id_modelo: data.id_modelo,
    nombre: data.nombre.trim(),
    peso: data.peso ?? 100,
    descripcion: data.descripcion ?? null
  }
  const result = await http.post<any>(url, payload)
  if (!result.ok) return null
  const json = result.data
  if (json?.id_componente) return json as Componente
  if (json?.status && json?.componente?.id_componente) return json.componente as Componente
  return null
}

/**
 * Crea componente.
 *
 * @returns { data: Componente | null; error?: string }
 */
export async function crearHijoComponenteService(
  baseUrl: string,
  padreId: number,
  data: { nombre: string; peso?: number | null; descripcion?: string | null; enfoque?: string | null; grupo_variante?: string | null }
): Promise<{ data: Componente | null; error?: string }> {
  const idNum = Number(padreId)
  if (!idNum || Number.isNaN(idNum)) {
    console.error('[crearHijoComponenteService] padreId inválido:', padreId)
    return { data: null, error: 'ID de componente padre inválido' }
  }
  const payload = {
    nombre: data.nombre.trim(),
    peso: data.peso != null ? Number(data.peso) : null,
    descripcion: data.descripcion ?? null,
    enfoque: data.enfoque ?? 'general',
    grupo_variante: data.grupo_variante ?? null
  }
  const result = await http.post<Componente>(`${baseUrl.replace(/\/$/, '')}/${idNum}/hijos`, payload)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `Error del servidor (HTTP ${result.status})`
    console.error('[crearHijoComponenteService] Error:', result.status, msg)
    return { data: null, error: msg }
  }
  return { data: result.data ?? null }
}

/**
 * Actualiza componente.
 *
 * @returns Componente | null
 */
export async function actualizarComponenteService(
  url: string,
  id_componente: number,
  data: Partial<Componente>
): Promise<Componente | null> {
  if (!id_componente) return null
  const payload = {
    ...data,
    nombre: data.nombre?.trim(),
    ordinal: data.ordinal?.trim(),
    peso: data.peso != null ? Number(data.peso) : null,
    id_tipo_componente: data.id_tipo_componente ?? null
  }
  const result = await http.put<any>(`${url.replace(/\/$/, '')}/${id_componente}`, payload)
  if (!result.ok) return null
  const resp = result.data
  // El controller devuelve el componente directamente (no envuelto en { status, componente })
  return resp?.id_componente ? (resp as Componente) : null
}

/**
 * Elimina componente · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarComponenteService(url: string, id: number): Promise<boolean> {
  const result = await http.delete<{ message?: string }>(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok
}

/**
 * Consulta componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `[]`. Un array
 * vacío NO distingue «sin datos» de «la petición falló».
 *
 * @returns Componente[]
 */
export async function getHijosComponenteService(url: string, id: number): Promise<Componente[]> {
  const result = await http.get<Componente[]>(`${url.replace(/\/$/, '')}/${id}/hijos`)
  return result.ok ? (result.data ?? []) : []
}

/**
 * Elimina componente · petición `DELETE`.
 *
 * No lanza: `true` solo si la petición tuvo éxito. `false` agrupa todos los
 * fallos posibles sin distinguirlos.
 *
 * @returns boolean
 */
export async function eliminarHijoComponenteService(url: string, padreId: number, hijoId: number): Promise<boolean> {
  const result = await http.delete<{ message?: string }>(`${url.replace(/\/$/, '')}/${padreId}/hijos/${hijoId}`)
  return result.ok
}

/**
 * Operación sobre componente · petición `POST`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns { hijos: Componente[] } | null
 */
export async function reasignarPesosComponenteService(
  url: string,
  padreId: number,
  pesos: Record<number, number>
): Promise<{ hijos: Componente[] } | null> {
  const result = await http.post<any>(`${url.replace(/\/$/, '')}/${padreId}/reasignar-pesos`, { pesos })
  if (!result.ok) return null
  return { hijos: result.data.hijos as Componente[] }
}

/**
 * Consulta componente · petición `GET`.
 *
 * No lanza: ante cualquier fallo (red, 4xx, 5xx) devuelve `null`. Un null
 * NO distingue «no encontrado» de «la petición falló».
 *
 * @returns Componente | null
 */
export async function getComponenteConHijosService(url: string, id: number): Promise<Componente | null> {
  const result = await http.get<Componente>(`${url.replace(/\/$/, '')}/${id}`)
  return result.ok ? (result.data ?? null) : null
}

/**
 * Reordena componente · petición `PUT`.
 *
 * @returns { ok: boolean; data?: Componente; message?: string }
 */
export async function reordenarComponenteService(
  url: string,
  id_componente: number,
  posicion: number
): Promise<{ ok: boolean; data?: Componente; message?: string }> {
  const result = await http.put<Componente>(`${url.replace(/\/$/, '')}/${id_componente}/reordenar`, { posicion })
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `HTTP ${result.status}`
    return { ok: false, message: msg }
  }
  return { ok: true, data: result.data }
}
